import Button from "@/common/components/Button";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import useUserPermissions from "../../hooks/useUserPermissions";
import useUpdateLeadContactResendEmail from "../../hooks/useUpdateLeadContactResendEmail";
import { updateContactResendEmailSchema, type LeadsFunnelUpdateContactResendEmailValidationSchema } from "../../schemas/leadFunnelUpdateResendEmail.schema";
import Modal from "../Modal";
import { ModalMessageSimple } from "../ModalMessageSimple";
import TextField from "../TextField";

interface ResendLeadContactEmailModalProps {
  contact: {
    id?: string;
    email?: string;
  };
  onSuccess: () => void;
};

const ResendLeadContactEmailModal = ({ contact, onSuccess }: ResendLeadContactEmailModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const { hasUserPermission } = useUserPermissions();

  const {
    mutate: updateContact,
    isPending: updateContactIsPending,
  } = useUpdateLeadContactResendEmail({
    contactId: contact?.id,
    onSuccess: () => {
      onSuccess()
      setIsOpen(false);
      setIsError(false);
      setMessages(['Email reenviado com sucesso!']);
    },
    onError: (error) => {
      setIsError(true);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    }
  });

  const {
    handleSubmit: handleSubmitResendEmail,
    reset: resetResendEmail,
    register: registerResendEmail,
    formState: { errors: errorsResendEmail },
  } = useForm<LeadsFunnelUpdateContactResendEmailValidationSchema>({
    resolver: zodResolver(updateContactResendEmailSchema)
  });

  const onSubmitResendEmail: SubmitHandler<LeadsFunnelUpdateContactResendEmailValidationSchema> = (data) => {
    updateContact(data);
  };

  return (
    hasUserPermission('Admin::Funnel::Contacts::ResendEmail') && (
      <div className="pt-[17px]">
        <Modal
          isOpen={isOpen}
          setIsOpen={(value) => {
            resetResendEmail();
            setIsOpen(value);
          }}
          Trigger={<Button>Reenviar</Button>}
          useCancellableFooter
          showClose={false}
          title="Reenviar Email"
          className="min-w-[720px]"
          subtitleClassName="mt-1 text-md text-white-300"
          cancelButtonClassName="bg-white-200 rounded-es-lg"
          confirmButtonClassName="bg-blue-700 rounded-ee-lg"
          confirmButtonIsLoading={updateContactIsPending}
          onCancel={() => setIsOpen(false)}
          onConfirm={handleSubmitResendEmail(onSubmitResendEmail)}
          body={(
            <form>
              <TextField
                label="Reenviar email"
                className="w-full"
                {...registerResendEmail('funnel_contact.email')}
                errorMessage={errorsResendEmail?.funnel_contact?.email?.message}
                defaultValue={contact?.email ?? ''}
                markAsRequired
              />
            </form>
          )}
        />

        {messages && (
          <ModalMessageSimple
            messages={messages}
            setMessages={setMessages}
            isError={isError}
          />
        )}
      </div>
    )
  );
}

export default ResendLeadContactEmailModal;
